import cn from 'classnames';
import React, {useEffect} from 'react';
import {renderToStaticMarkup} from 'react-dom/server';

import {AppButton} from '../../../components/Button';
import ContactSupportLink from '../../../components/ContactSupportLink/ContactSupportLink';
import InstructionModalTemplate from '../../../components/DataDisplay/InstructionModalTemplate';
import ManualActivation from '../../../components/ManualActivation/ManualActivation';
import useManualActivation from '../../../components/ManualActivation/useManualActivation';
import PopupFullPage from '../../../components/Popup/PopupFullPage';
import SupportAndStoreTemplate from '../../../components/SupportAndStoreTemplate/SupportAndStoreTemplate';
import pairingErrors from '../../../consts/device/pairingErrors';
import * as localizationTokens from '../../../consts/localization/localizationTokens';
import useSuccessNotificationsRemover from '../../../hooks/useSuccessNotificationsRemover';
import {amplitudeConnectionTracking} from '../../../services/amplitude';
import analyticsService from '../../../services/analyticsService';
import connectivityService from '../../../services/device/connectivityService';
import deviceTypesService from '../../../services/device/deviceTypesService';
import {getLocalizedStrings} from '../../../services/localization/localizationService';
import pairingGuidePageService from '../../../services/pairingGuidePageService';
import pairingService from '../../../services/pairingService';
import appRouterService from '../../../services/route/appRouterService';
import elementsTesIds from '../../../test/consts/elementsTesIds';
import {pairingGuideErrorsContent} from './pairingGuideErrorsContent';

const PairingGuideErrorPopup = (props) => {
    const {deviceType, error, setError, retryNumber = 0} = props;
    const {type} = error;
    const errorSet = getErrorSet(type, deviceType);
    const deviceTypesData = deviceTypesService.getDeviceTypesDataByType(deviceType);
    const deviceName = deviceTypesData?.name;
    const isManualActivationAvailable = useManualActivation();
    const isNotSupportedDevice = type === pairingErrors.ERROR_NOT_SUPPORTED_DEVICE.type;
    const isConnectionNotFound = type === pairingErrors.ERROR_CONNECTION_NOT_FOUND.type;
    const isPairingError = type === pairingErrors.ERROR_PAIRING.type;
    const isContactSupportLinkNeeded = isConnectionNotFound || isPairingError;

    let replacedDescription;

    useEffect(() => {
        if (isConnectionNotFound) {
            amplitudeConnectionTracking.trackDevicePairingError(deviceType);
        }
    }, []);

    useEffect(() => {
        const isBle = connectivityService.isBle();

        analyticsService.pushPairingFailedEvent(type, deviceType, isBle);
    }, [type]);

    useSuccessNotificationsRemover();

    if (!errorSet) return null;

    const localizedStrings = getLocalizedStrings();
    const {description, mainButtonText, secondaryButtonText, tertiaryButtonText, title, subtitleText} = errorSet;

    if (isContactSupportLinkNeeded && description) {
        const descriptionText = localizedStrings[description];

        replacedDescription = descriptionText.replace(
            localizationTokens.CONTACT_SUPPORT_LINK,
            renderToStaticMarkup(<ContactSupportLink />)
        );
    }

    const onTryAgainNotSupportedDevice = () => {
        pairingService.disconnectAndClearState();
        appRouterService.forwardToSelectDevice();
    };

    const onTryAgainUsbBtnClick = () => {
        pairingService.disconnectAndClearState();
        connectivityService.setIsUsb();
        appRouterService.forwardToConnectionGuidePage({deviceType, isNew: true});
        setError(null);
    };

    const onTryAgainBleBtnClick = () => {
        pairingService.disconnectAndClearState();
        connectivityService.setIsBle();
        appRouterService.forwardToPairingGuidePage({deviceType, isNew: true, retryNumber: parseInt(retryNumber) + 1});
        setError(null);
    };

    const onTryAgainBtnClick = () => {
        const isBle = connectivityService.isBle();

        if (isBle) {
            onTryAgainBleBtnClick();
        } else {
            onTryAgainUsbBtnClick();
        }
    };

    const onSkipBtnClick = () => {
        pairingService.disconnectAndClearState();
        appRouterService.forwardToMyDevicesPage();
    };

    const {
        mainButtonProp: {onClick: onMainButtonClick, testId: mainButtonTestId},
        secondaryButtonProp: {onClick: onSecondaryButtonClick, testId: secondaryButtonTestId},
        tertiaryButtonProp: {onClick: onTertiaryButtonClick, testId: tertiaryButtonTestId},
    } = pairingGuidePageService.getPairingButtonProp({
        usbProp: {onClick: onTryAgainUsbBtnClick, testId: elementsTesIds.PAIRING_SEQUENCE_ERROR_BTN_TRY_AGAIN_USB},
        bleProp: {onClick: onTryAgainBleBtnClick, testId: elementsTesIds.PAIRING_SEQUENCE_ERROR_BTN_TRY_AGAIN_BLE},
        commonProp: {onClick: onTryAgainBtnClick, testId: elementsTesIds.PAIRING_SEQUENCE_ERROR_TRY_AGAIN_BTN},
        cancelProp: {onClick: onSkipBtnClick, testId: elementsTesIds.PAIRING_SEQUENCE_ERROR_SKIP_BTN},
        deviceType,
    });

    const titleText = localizedStrings.formatString(localizedStrings[title], deviceName);

    return (
        <PopupFullPage contentLabel={titleText}>
            <InstructionModalTemplate
                className='mx-auto w-full py-10'
                subTitleKey={subtitleText}
                instructionList={description}
                instructionText={replacedDescription}
                articleProps={{
                    titleText,
                    className: 'lg:max-w-[724px]',
                    error: true,
                }}
                instructionContent={
                    <>
                        <AppButton
                            primary
                            light
                            className='mt-10'
                            testId={mainButtonTestId}
                            onClick={isNotSupportedDevice ? onTryAgainNotSupportedDevice : onMainButtonClick}
                        >
                            {localizedStrings[mainButtonText]}
                        </AppButton>
                        <AppButton
                            secondary
                            light
                            className='mt-3'
                            testId={secondaryButtonTestId}
                            onClick={isNotSupportedDevice ? onSkipBtnClick : onSecondaryButtonClick}
                        >
                            {localizedStrings[secondaryButtonText]}
                        </AppButton>
                        {tertiaryButtonText && (
                            <AppButton
                                tertiary
                                className='mt-3'
                                onClick={onTertiaryButtonClick}
                                testId={tertiaryButtonTestId}
                            >
                                {localizedStrings[tertiaryButtonText]}
                            </AppButton>
                        )}
                    </>
                }
            >
                {isManualActivationAvailable && <ManualActivation />}
                <SupportAndStoreTemplate className={cn({'mt-10': isManualActivationAvailable})} />
            </InstructionModalTemplate>
        </PopupFullPage>
    );
};

const getErrorSet = (type, deviceType) => {
    const {PAIRING_ERRORS, CONNECTION_ERRORS, DEVICE_SUPPORT_FAILED_ERRORS, WRONG_DEVICE_ERRORS} =
        pairingGuideErrorsContent(deviceType);

    const wrongDeviceErrors = WRONG_DEVICE_ERRORS[deviceType];
    const getRestOfContent = ({mainButtonText, secondaryButtonText}) => ({mainButtonText, secondaryButtonText});

    switch (type) {
        case pairingErrors.ERROR_CONNECTION_NOT_FOUND.type:
            return CONNECTION_ERRORS;
        case pairingErrors.ERROR_PAIRING.type:
            return PAIRING_ERRORS;
        case pairingErrors.ERROR_NOT_SUPPORTED_DEVICE.type:
            return DEVICE_SUPPORT_FAILED_ERRORS;
        case pairingErrors.ERROR_WRONG_DEVICE.type:
            return {...wrongDeviceErrors, ...getRestOfContent(WRONG_DEVICE_ERRORS)};
        default:
            return null;
    }
};

export default PairingGuideErrorPopup;
